import React, { useState, useEffect } from "react";
import { Grid, Checkbox } from "semantic-ui-react";
import styles from "./styles.module.scss";
import { Link } from "react-router-dom";
import ClearingFundSummary from "../ClearingFundSummary";
import Summary from "../Summary";
import { CFItem, ReportValues, SummaryReport } from "../../types";
import currency from "currency.js";
import Spinner from "../Spinner";

export interface SummarySectionProps {
  date: string;
  cfObj: SummaryReport;
  cfApexObj: SummaryReport;
  mtmObj: SummaryReport;
  varObj: SummaryReport;
  varApexObj: SummaryReport;
  excessApex: SummaryReport;
  excess: SummaryReport;
  summary: CFItem;
  loading: boolean;
}

const SummaryBar = ({
  date,
  cfObj,
  cfApexObj,
  varApexObj,
  excessApex,
  mtmObj,
  varObj,
  excess,
  summary,
  loading,
}: SummarySectionProps) => {
  const [projected, setProjected] = useState(false);
  const [haveprojected, setHaveProjected] = useState(false);
  const [displayVarObj, setDisplayVarObj] = useState(varObj);
  const [displayExcessObj, setDisplayExcessObj] = useState(excess);
  const [displayCfObj, setDisplayCfObj] = useState(cfObj);

  useEffect(() => {
    if (varApexObj.sliceId !== "" && varApexObj.sliceId !== "-") {
      setHaveProjected(true);
    }
  }, [varApexObj]);

  useEffect(() => {
    if (projected) {
      setDisplayVarObj(varApexObj);
      setDisplayExcessObj(excessApex);
      setDisplayCfObj(cfApexObj);
    } else {
      setDisplayVarObj(varObj);
      setDisplayExcessObj(excess);
      setDisplayCfObj(cfObj);
    }
  }, [projected, varApexObj, excessApex, cfApexObj, varObj, excess, cfObj]);

  const handleToggle = () => {
    setProjected(!projected);
  };

  return (
    <Grid divided>
      <Grid.Row columns="equal">
        <Grid.Column>
          <h2 className={styles.summary}>CF Summary</h2>
          <Link to={`?level=cf`}>
            <div
              id={projected ? "apex-cf-summary" : "cf-summary"}
              className={projected ? styles.projectedView : ""}
            >
              <Spinner loading={loading}>
                <ClearingFundSummary
                  date={date}
                  sliceId="Latest"
                  requirement={
                    isNaN(Number(displayCfObj.reportValues.current))
                      ? displayCfObj.reportValues.current
                      : currency(displayCfObj.reportValues.current).format()
                  }
                  reqChg={displayCfObj.reportValues["%change"]}
                  excess={
                    isNaN(Number(displayExcessObj.reportValues.current))
                      ? displayExcessObj.reportValues.current
                      : currency(displayExcessObj.reportValues.current).format()
                  }
                  summary={summary}
                  projected={projected}
                />
              </Spinner>
            </div>
          </Link>
        </Grid.Column>
        <Grid.Column>
          <h2 className={styles.summary}>
            VaR Summary{" "}
            <Checkbox
              className={styles.projectedSwitch}
              id="projected-switch"
              label="Projected"
              disabled={!haveprojected}
              checked={projected}
              toggle
              onChange={handleToggle}
            />
          </h2>
          <Link to={`?level=var`}>
            <div
              id={projected ? "apex-var-summary" : "var-summary"}
              className={projected ? styles.projectedView : ""}
            >
              <Spinner loading={loading}>
                <Summary
                  date={date}
                  sliceId={displayVarObj.sliceId}
                  requirement={
                    isNaN(Number(displayVarObj.reportValues.current))
                      ? displayVarObj.reportValues.current
                      : currency(displayVarObj.reportValues.current).format()
                  }
                  reqChg={displayVarObj.reportValues["%change"]}
                  reqPerChg="Coming Soon!"
                  highRange={currency(
                    projected
                      ? (summary["apex-trend"]?.var?.totalRequirement
                          .max as string)
                      : (summary.trend?.var?.totalRequirement.max as string)
                  ).format()}
                  lowRange={currency(
                    projected
                      ? (summary["apex-trend"]?.var?.totalRequirement
                          .max as string)
                      : (summary.trend?.var?.totalRequirement.min as string)
                  ).format()}
                />
              </Spinner>
            </div>
          </Link>
        </Grid.Column>
        <Grid.Column>
          <h2 className={styles.summary}>MtM Summary</h2>
          <Link to={`?level=mtm`}>
            <div id={"mtm-summary"}>
              <Spinner loading={loading}>
                <Summary
                  date={date}
                  sliceId={varObj.sliceId}
                  requirement={
                    isNaN(Number(mtmObj.reportValues.current))
                      ? mtmObj.reportValues.current
                      : currency(mtmObj.reportValues.current).format()
                  }
                  reqChg={mtmObj.reportValues["%change"]}
                  reqPerChg="Coming Soon!"
                  highRange={currency(
                    summary.trend?.mtm?.totalRequirement.max as string
                  ).format()}
                  lowRange={currency(
                    summary.trend?.mtm?.totalRequirement.min as string
                  ).format()}
                />
              </Spinner>
            </div>
          </Link>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default SummaryBar;
